import React, { useEffect, useRef, useState } from "react"
import { AuthCard, AuthLayout } from "components/views/login/Shared"
import { useFormContext } from "react-hook-form"
import { CompleteFormInput } from "components/views/atoms/forms/FormInput"
import { Spacer } from "components/views/atoms/Atoms"
import { Body } from "components/views/atoms/Bodies"
import ErrorMessageMarkdown from "components/views/login/ErrorMessageMarkdown"
import { useSignUpFindAccount } from "./context/SignUpFindAccountContext"
import { TextLink } from "../views/atoms/links/TextLink"
import queryString from "query-string"
import { useHistory, useLocation } from "react-router"
import { DateOfBirthWithLabels } from "../views/modal/BenefitEnrollmentFlow/ConnectInsuranceStep/DateOfBirthWithLabels"
import { LeftBackArrowImage } from "../views/fle/Molecules"
import { ButtonPrimary } from "../views/atoms/buttons/ButtonPrimary"
import L10n from "../../lib/carrot-l10n"
import { H1, H2 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const FirstNameInput = ({ enabled, firstName }) => {
  const { register, errors } = useFormContext()

  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "First Name is required" })}
      errors={errors}
      showValidation={false}
      defaultValue={firstName}
      id="firstName"
      name="firstName"
      label="First Name"
      type="firstName"
      showAsterisk
      autoFocus
    />
  )
}

const LastNameInput = ({ enabled, lastName }) => {
  const { register, errors } = useFormContext()

  return (
    <CompleteFormInput
      readOnly={!enabled}
      ref={register({ required: "Last name is required" })}
      errors={errors}
      showValidation={false}
      defaultValue={lastName}
      id="lastName"
      name="lastName"
      label="Last Name"
      type="lastName"
      showAsterisk
    />
  )
}

function FindAccountBackArrow() {
  const history = useHistory()
  return (
    <div
      className="pointer bg-carrot-orange-01 br-100 flex items-center justify-center"
      style={{
        height: "2rem",
        width: "2rem"
      }}
      onClick={() => history.push("/signup")}
    >
      <LeftBackArrowImage />
    </div>
  )
}

function SignUpHeader() {
  const { errorMessage, showTryAgain } = useSignUpFindAccount()

  return (
    <>
      <FindAccountBackArrow />
      <Spacer height={1.5} />
      {showTryAgain ? <H1>{l`Try again`}</H1> : <H1>{l`Let's find your account`}</H1>}
      <Spacer height={0.5} />
      {errorMessage ? (
        <ErrorMessageMarkdown align={"tl"} source={errorMessage} />
      ) : (
        <>
          {showTryAgain ? (
            <>
              <Body color="black-80">{l`Sorry, we couldn't find your account with the information provided.`}</Body>
              <Spacer height={1.5} />
              <Body color="black-80">
                {l`Try again or`}&nbsp;
                <TextLink to="/signup-get-help">{l`contact our Care Team`}</TextLink>
                {l` for help.`}
              </Body>
            </>
          ) : (
            <Body color="black-80">{l`We can't find your email address, so let's use other
                  information to find your account.`}</Body>
          )}
        </>
      )}
    </>
  )
}

const FindAccountButton = ({ submitWithDob, inputsEnabled }) => (
  <div className="flex flex-auto justify-center">
    <ButtonPrimary enabled={inputsEnabled} onClick={submitWithDob}>{l`Find account`}</ButtonPrimary>
  </div>
)

const SignUpFindAccountForm = ({ email }) => {
  const { shake, inputsEnabled, onSubmit } = useSignUpFindAccount()
  const { setValue, register } = useFormContext()
  const dateOfBirthRef = useRef()
  const [dateOfBirth, setDateOfBirth] = useState(null)
  const [validDateOfBirth, setValidDateOfBirth] = useState(true)

  function isDateOfBirthValid() {
    // @ts-ignore
    const dobInputIsValid = dateOfBirthRef.current.validate()
    setValidDateOfBirth(dobInputIsValid)
    return dobInputIsValid
  }

  useEffect(() => {
    setValue("email", email)
  }, [])

  const submitWithDob = (e) => {
    e.preventDefault()
    if (!isDateOfBirthValid()) return
    // @ts-ignore
    setDateOfBirth(dateOfBirthRef.current.getMoment())
    // @ts-ignore
    const dobVal = dateOfBirthRef.current.getMoment().format("YYYY-MM-DD")
    onSubmit(dobVal)
  }

  return (
    <AuthCard shake={shake} onSubmit={submitWithDob}>
      <Spacer height={1.5} />
      <SignUpHeader />
      <Spacer height={3} />
      <H2 variant="dark">{l`What's your name?`}</H2>
      <Spacer height={1} />
      <input hidden ref={register()} name="email" />
      <div className="flex flex-row">
        <div className="flex-auto" style={{ flexBasis: 0 }}>
          <FirstNameInput enabled={inputsEnabled} firstName={""} />
        </div>
        <Spacer width={1} />
        <div className="flex-auto" style={{ flexBasis: 0 }}>
          <LastNameInput enabled={inputsEnabled} lastName={""} />
        </div>
      </div>
      <Spacer height={3} />
      <H2 variant="dark">{l`What's your date of birth?`}</H2>
      <Spacer height={1} />
      <DateOfBirthWithLabels showAsterisk {...{ dateOfBirthRef, dateOfBirth, validDateOfBirth }} />
      <Spacer height={3} />
      <FindAccountButton {...{ inputsEnabled, submitWithDob }} />
      <Spacer height={3} />
    </AuthCard>
  )
}

const SignUpFindAccountPage = () => {
  const location = useLocation()
  const history = useHistory()
  const { email } = queryString.parse(location.search)

  useEffect(() => {
    if (!email) {
      history.push("/signup")
    }
  }, [])
  return (
    <AuthLayout title={l`client.signUp.short`}>
      <SignUpFindAccountForm email={email} />
    </AuthLayout>
  )
}

export default SignUpFindAccountPage
